import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { limitFragment } from '@/fragments/Limits';
import { LimitTypeEnum } from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  RemovePlayerWithdrawalLimitMutation,
  RemovePlayerWithdrawalLimitMutationVariables,
} from './__generated__/PlayerRemoveWithdrawalLimitForm';

const query = graphql`
  query SanityPlayerRemoveWithdrawalLimitForm {
    sanityPlayerRemoveWithdrawalLimitsForm {
      title {
        ...SanityLocaleString
      }
    }
  }
`;

const removeWithdrawalLimitMutation = gql`
  mutation RemovePlayerWithdrawalLimit(
    $playerId: ID!
    $limitPeriodType: LimitTypeEnum!
  ) {
    removeWithdrawalLimit(
      playerId: $playerId
      limitPeriodType: $limitPeriodType
    ) {
      id
      withdrawalLimit {
        dayLimit {
          ...PeriodLimit
        }
        weekLimit {
          ...PeriodLimit
        }
        monthLimit {
          ...PeriodLimit
        }
      }
    }
  }
  ${limitFragment}
`;

const PlayerRemoveWithdrawalLimitForm: FC<{
  playerId: string;
  limitPeriodType: LimitTypeEnum;
}> = ({ playerId, limitPeriodType }) => {
  const { close } = useModalContext();
  const { t } = useTranslate();
  const staticData =
    useStaticQuery<Queries.SanityPlayerRemoveWithdrawalLimitFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerRemoveWithdrawalLimitsForm;

  const [removePlayerWithdrawalLimitState, removePlayerWithdrawalLimit] =
    useMutation<
      RemovePlayerWithdrawalLimitMutation,
      RemovePlayerWithdrawalLimitMutationVariables
    >(removeWithdrawalLimitMutation);

  const onSubmit = () => {
    if (playerId) {
      removePlayerWithdrawalLimit({
        playerId,
        limitPeriodType,
      }).then((res) => {
        if (res.error?.message && isMounted) {
          setErrorMessage(res.error?.message);
        } else if (close) {
          close();
        }
      });
    }
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="flex flex-col gap-4 p-3">
        <Form
          defaultValues={{}}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
          description="This action will reset the withdrawal limit to the default value."
        >
          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value="Remove"
            disabled={removePlayerWithdrawalLimitState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerRemoveWithdrawalLimitForm;
